import type { IconDefinition } from 'fontawesome/free-solid-svg-icons'
import {
    faArrowDown,
    faArrowUp,
    faArrowRight,
} from 'fontawesome/free-solid-svg-icons'
import {
    MOST_IMPROVED_TEAM_MEMBER,
    MOST_POSITIVE_LONG_FORM_RESPONSES,
    MOST_PROMOTER_RESPONSES,
    SEND_METHOD_EMAIL,
    SEND_METHOD_INTERNAL_NOTE,
    SEND_METHOD_SLACK,
    SEND_METHOD_TEAMS,
    PraiseActionGenerator,
    PraiseActionSendMethodKey,
    TeamMember,
} from '@/entities/frontlineManagerDash'
import DOMPurify from 'dompurify'

// Description for screen readers
export const getAriaScoreDesc = (
    newScore: number,
    scoreChange: number,
    timePeriod: string,
    metric: string
): string => {
    const time = timePeriod ? `For the last ${timePeriod}, ` : ''
    const currentScore = `this topic's ${metric ?? ''} score is ${newScore}. `

    const prevScore = scoreChange
        ? `Compared to last period it has ${scoreChange > 0 ? 'increased' : 'decreased'} by ${Math.abs(scoreChange)}.`
        : 'It has not changed since compared to the last period.'

    return `${time}${currentScore}${prevScore}`
}

export const getDiffIcon = (change: number): IconDefinition => {
    return change === 0 ? faArrowRight : change > 0 ? faArrowUp : faArrowDown
}

export const getDiffVariant = (change: number): string => {
    return change === 0 ? 'neutral' : change > 0 ? 'positive' : 'negative'
}

// Used for Amplitude track events
export const getPraiseGeneratorLabel = (
    generator: PraiseActionGenerator
): string => {
    switch (generator) {
        case MOST_IMPROVED_TEAM_MEMBER:
            return 'mostImproved'
        case MOST_PROMOTER_RESPONSES:
            return 'mostPromoters'
        case MOST_POSITIVE_LONG_FORM_RESPONSES:
            return 'dynamic'
        default:
            return ''
    }
}
// Used for Amplitude track events
export const getSendMethodLabel = (
    method: PraiseActionSendMethodKey
): string => {
    switch (method) {
        case SEND_METHOD_EMAIL:
            return 'email'
        case SEND_METHOD_INTERNAL_NOTE:
            return 'note'
        case SEND_METHOD_SLACK:
            return 'slack'
        case SEND_METHOD_TEAMS:
            return 'msteams'
        default:
            return ''
    }
}

export const escapeSpecialChars = (str: string): string => {
    return str.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&')
}

export const atMentionRegex = (name: string): RegExp => {
    return new RegExp(`(?:\\s|^)@${name}\\b`, 'g')
}

export const hashtagRegex = /(?:\s|^)#([^\]]\w+)\b/g
export const reformatHashtags = (str: string): string => {
    return str.replace(hashtagRegex, '[#$1](tag:$1)')
}

/**
 * Process mentions and tags in a praise message
 * @param teamMembers List of team members
 * @param message Praise message with unprocessed mentions/tags
 */
export const processMentionsAndTags = (
    message: string,
    teamMembers: TeamMember[]
): string => {
    if (!teamMembers.length || !message) {
        return message
    }

    message = DOMPurify.sanitize(message) // Sanitize message

    for (const member of teamMembers) {
        if (!member?.name || !member?.email) {
            continue
        }

        const escapedName = escapeSpecialChars(member.name)
        const email = member.email ?? member.name
        message = message.replace(
            atMentionRegex(escapedName),
            `[@${member.name}](user:${email})`
        )
    }

    return reformatHashtags(message)
}

/**
 * Check if the message contains mentions or tags
 * @param message
 * @param teamMembers
 */
export const doesMessageHaveMentionsOrTags = (
    message: string,
    teamMembers: TeamMember[]
) => {
    if (!message) {
        return false
    }

    for (const member of teamMembers) {
        if (!member?.name) {
            continue
        }

        const escapedName = escapeSpecialChars(member.name)
        if (atMentionRegex(escapedName).test(message)) {
            return true
        }
    }

    return hashtagRegex.test(message)
}

/**
 * Return a list of emails separated by semicolon, comma, or whitespace and remove empty strings
 *
 * @param emails String of emails separated by semicolon, comma, or whitespace
 * @returns Formatted list of emails
 */
export const formatEmailList = (emails: string): string[] => {
    return (emails ?? '').split(/[;, ]/).filter((email) => email?.trim?.())
}
